import { ReactNode, useEffect } from 'react';

/**
 * OmniRetail — shared modal / dialog shell.
 *
 * Renders the full-screen backdrop and the centered panel. By design it does
 * NOT close when the backdrop is clicked — that prevents the accidental data
 * loss reported in QA #18, where a misclick outside a form discarded everything
 * typed. Callers close it with an explicit Cancel / Close / ✕ control that calls
 * `onClose`. A read-only viewer can opt back into click-outside with
 * `closeOnBackdrop`.
 *
 * `overlayClassName` and `className` carry the backdrop and panel styling. The
 * migrated call sites pass their original class strings, so nothing changes
 * visually; the defaults match the app's standard modal for any new usage.
 */
export function Modal({
  onClose,
  children,
  className = 'bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden',
  overlayClassName = 'fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4',
  closeOnBackdrop = false,
  closeOnEsc = true,
}: {
  onClose: () => void;
  children: ReactNode;
  className?: string;
  overlayClassName?: string;
  closeOnBackdrop?: boolean;
  closeOnEsc?: boolean;
}) {
  // Lock background scroll while the modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Escape closes — a deliberate keypress, unlike an accidental backdrop click.
  useEffect(() => {
    if (!closeOnEsc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeOnEsc, onClose]);

  return (
    <div
      className={overlayClassName}
      onClick={closeOnBackdrop ? onClose : undefined}
      role="dialog"
      aria-modal="true"
    >
      <div className={className} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}
